import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/useAuth';
import { recordLoginOnce, sendHeartbeat } from '../api/analytics';

const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Records login once per browser session and sends visibility-aware heartbeats
 * for platform time-on-site statistics.
 */
export function useAnalytics(): void {
  const { isAuthenticated, loading } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (loading || !isAuthenticated) {
      return;
    }

    let cancelled = false;

    const ping = async () => {
      if (cancelled || document.visibilityState !== 'visible') {
        return;
      }
      try {
        await sendHeartbeat();
      } catch {
        // Non-critical; ignore transient auth/network errors
      }
    };

    const start = async () => {
      try {
        await recordLoginOnce();
        await ping();
      } catch {
        // Login record is best-effort
      }
      if (cancelled) {
        return;
      }
      intervalRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    };

    start();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void ping();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, loading]);
}
